import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Shield, Eye, FileCheck, AlertTriangle, Send, CheckCircle2, Star, CheckCircle, Loader2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { liverySchemeInfo } from '../data/mock';
import { submitWelfareReport } from '../services/api';

const LiverySchemePage = () => {
  const [reportForm, setReportForm] = useState({
    yardName: '',
    location: '',
    concernType: '',
    description: '',
    anonymous: true,
    contactEmail: ''
  });
  const [reportSubmitted, setReportSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleReportSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      await submitWelfareReport(reportForm);
      setReportSubmitted(true);
    } catch (err) {
      setError(err.message || 'Failed to submit report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main>
      <div className="page-header">
        <div className="container">
          <div className="breadcrumb">
            <Link to="/">Home</Link>
            <ChevronRight size={16} />
            <span>Livery Yard Membership Scheme</span>
          </div>
          <h1 className="display-medium">{liverySchemeInfo.title}</h1>
          <p className="body-large">{liverySchemeInfo.description}</p>
        </div>
      </div>

      <section className="section">
        <div className="container">
          <Tabs defaultValue="about" className="w-full">
            <TabsList 
              style={{ 
                display: 'flex', 
                gap: '8px', 
                background: 'white', 
                padding: '8px', 
                borderRadius: '16px',
                marginBottom: '40px',
                justifyContent: 'center',
                flexWrap: 'wrap'
              }}
            >
              <TabsTrigger 
                value="about"
                style={{ 
                  padding: '12px 24px', 
                  borderRadius: '12px',
                  fontWeight: 500
                }}
              >
                How It Works
              </TabsTrigger>
              <TabsTrigger 
                value="inspections"
                style={{ 
                  padding: '12px 24px', 
                  borderRadius: '12px',
                  fontWeight: 500
                }}
              >
                Inspections
              </TabsTrigger>
              <TabsTrigger 
                value="ratings"
                style={{ 
                  padding: '12px 24px', 
                  borderRadius: '12px',
                  fontWeight: 500
                }}
              >
                Public Ratings
              </TabsTrigger>
              <TabsTrigger 
                value="report"
                style={{ 
                  padding: '12px 24px', 
                  borderRadius: '12px',
                  fontWeight: 500
                }}
              >
                Report a Concern
              </TabsTrigger>
            </TabsList>

            {/* How It Works */}
            <TabsContent value="about">
              <div style={{ maxWidth: '900px', margin: '0 auto' }}>
                <div className="network-card" style={{ marginBottom: '32px' }}>
                  <h2 className="heading-2" style={{ marginBottom: '24px' }}>About the Scheme</h2>
                  <p className="body-medium" style={{ marginBottom: '16px' }}>
                    The Livery Yard Membership Scheme is a voluntary programme designed to promote and recognise high welfare standards at livery yards across Scotland.
                  </p>
                  <p className="body-medium" style={{ marginBottom: '24px' }}>
                    Member yards commit to meeting our welfare standards and agree to regular inspections. In return, they receive recognition and support, while horse owners gain access to transparent information about yard standards.
                  </p>
                  
                  <h3 className="heading-3" style={{ marginBottom: '16px' }}>Key Features</h3>
                  <ul style={{ paddingLeft: '0', listStyle: 'none' }}>
                    {liverySchemeInfo.features.map((feature, index) => (
                      <li 
                        key={index}
                        style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '12px', 
                          marginBottom: '12px' 
                        }}
                      >
                        <CheckCircle size={20} color="var(--brand-dark)" />
                        <span className="body-medium">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="grid-3">
                  {[
                    { icon: Shield, title: 'For Yard Owners', desc: 'Demonstrate your commitment to horse welfare and attract responsible livery clients.' },
                    { icon: Eye, title: 'For Horse Owners', desc: 'Make informed decisions about where to keep your horse based on verified standards.' },
                    { icon: FileCheck, title: 'For Horses', desc: 'Help raise welfare standards across Scotland for all horses in livery.' }
                  ].map((item, index) => (
                    <div key={index} className="network-card" style={{ textAlign: 'center' }}>
                      <item.icon size={32} color="var(--brand-dark)" style={{ marginBottom: '16px' }} />
                      <h4 className="heading-3" style={{ marginBottom: '8px' }}>{item.title}</h4>
                      <p className="body-small">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            {/* Inspections */}
            <TabsContent value="inspections">
              <div style={{ maxWidth: '800px', margin: '0 auto' }}>
                <div className="network-card">
                  <h2 className="heading-2" style={{ marginBottom: '24px' }}>Inspection Process</h2>
                  <p className="body-medium" style={{ marginBottom: '24px' }}>
                    Our inspections are carried out by qualified assessors with extensive equine welfare experience. Inspections are fair, consistent, and designed to be supportive rather than punitive.
                  </p>

                  <h3 className="heading-3" style={{ marginBottom: '16px' }}>What We Assess</h3>
                  <div className="grid-2" style={{ marginBottom: '32px' }}>
                    {[
                      'Stable and field conditions',
                      'Fencing and security',
                      'Water and feed provision',
                      'Shelter and turnout access',
                      'Health and safety measures',
                      'Emergency procedures',
                      'Record keeping',
                      'Overall horse welfare'
                    ].map((item, index) => (
                      <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <CheckCircle size={18} color="var(--brand-dark)" />
                        <span className="body-medium">{item}</span>
                      </div>
                    ))}
                  </div>

                  <h3 className="heading-3" style={{ marginBottom: '16px' }}>Inspection Frequency</h3>
                  <p className="body-medium">
                    Member yards receive an initial comprehensive inspection and annual follow-up visits. Additional spot-checks may be conducted if concerns are raised.
                  </p>
                </div>
              </div>
            </TabsContent>

            {/* Public Ratings */}
            <TabsContent value="ratings">
              <div style={{ maxWidth: '800px', margin: '0 auto' }}>
                <div className="network-card">
                  <h2 className="heading-2" style={{ marginBottom: '24px' }}>Public Ratings System</h2>
                  
                  <div 
                    style={{ 
                      background: '#FEF3C7', 
                      padding: '20px', 
                      borderRadius: '12px', 
                      marginBottom: '24px',
                      display: 'flex',
                      gap: '12px'
                    }}
                  >
                    <AlertTriangle size={24} color="#92400E" style={{ flexShrink: 0 }} />
                    <div>
                      <p className="body-medium" style={{ color: '#92400E', fontWeight: 600 }}>
                        Coming Soon
                      </p>
                      <p className="body-small" style={{ color: '#92400E' }}>
                        Our public ratings system is currently in development. Check back for updates.
                      </p>
                    </div>
                  </div>

                  <p className="body-medium" style={{ marginBottom: '24px' }}>
                    Once launched, our transparent ratings system will allow horse owners to view inspection results and welfare ratings for member yards, helping them make informed choices about livery.
                  </p>

                  <h3 className="heading-3" style={{ marginBottom: '16px' }}>Rating Categories</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {[
                      { stars: 5, label: 'Excellent', desc: 'Exceeds all welfare standards' },
                      { stars: 4, label: 'Good', desc: 'Meets all welfare standards with some areas of excellence' },
                      { stars: 3, label: 'Satisfactory', desc: 'Meets minimum welfare standards' }
                    ].map((rating, index) => (
                      <div 
                        key={index}
                        style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '16px',
                          padding: '16px',
                          background: 'var(--bg-subtle)',
                          borderRadius: '12px'
                        }}
                      >
                        <div style={{ display: 'flex', gap: '2px' }}>
                          {[...Array(rating.stars)].map((_, i) => (
                            <Star key={i} size={18} fill="var(--brand-dark)" color="var(--brand-dark)" />
                          ))}
                        </div>
                        <div>
                          <span className="body-medium" style={{ fontWeight: 600 }}>{rating.label}</span>
                          <span className="body-small" style={{ marginLeft: '8px' }}>– {rating.desc}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Report a Concern */}
            <TabsContent value="report">
              <div style={{ maxWidth: '700px', margin: '0 auto' }}>
                {reportSubmitted ? (
                  <div className="success-message" style={{ padding: '48px', textAlign: 'center' }}>
                    <CheckCircle2 size={48} color="#10b981" style={{ marginBottom: '16px' }} />
                    <h2 className="heading-2" style={{ marginBottom: '16px' }}>Report Received</h2>
                    <p className="body-medium" style={{ marginBottom: '24px' }}>
                      Thank you for your report. We take all welfare concerns seriously and will investigate appropriately.
                    </p>
                    <button 
                      onClick={() => {
                        setReportSubmitted(false);
                        setReportForm({
                          yardName: '',
                          location: '',
                          concernType: '',
                          description: '',
                          anonymous: true,
                          contactEmail: ''
                        });
                      }}
                      className="btn-secondary"
                    >
                      Submit Another Report
                    </button>
                  </div>
                ) : (
                  <div className="network-card" style={{ padding: '40px' }}>
                    <h2 className="heading-2" style={{ marginBottom: '8px' }}>Report a Welfare Concern</h2>
                    <p className="body-small" style={{ marginBottom: '32px' }}>
                      Use this form to report welfare concerns at any livery yard. Reports can be made anonymously.
                    </p>

                    <div 
                      style={{ 
                        background: '#FEE2E2', 
                        padding: '16px', 
                        borderRadius: '12px', 
                        marginBottom: '24px',
                        display: 'flex',
                        gap: '12px'
                      }}
                    >
                      <AlertTriangle size={20} color="#DC2626" style={{ flexShrink: 0, marginTop: '2px' }} />
                      <p className="body-small" style={{ color: '#DC2626' }}>
                        <strong>For emergencies:</strong> If a horse is in immediate danger, please contact the SSPCA (03000 999 999) or call 999.
                      </p>
                    </div>

                    {error && (
                      <div 
                        style={{ 
                          background: '#FEE2E2', 
                          padding: '16px', 
                          borderRadius: '12px', 
                          marginBottom: '24px',
                          color: '#DC2626'
                        }}
                      >
                        {error}
                      </div>
                    )}

                    <form onSubmit={handleReportSubmit}>
                      <div className="form-group">
                        <Label htmlFor="yardName">Yard Name *</Label>
                        <Input 
                          id="yardName"
                          value={reportForm.yardName}
                          onChange={(e) => setReportForm(prev => ({ ...prev, yardName: e.target.value }))}
                          required
                          style={{ marginTop: '8px' }}
                        />
                      </div>

                      <div className="form-group">
                        <Label htmlFor="location">Location / Area *</Label>
                        <Input 
                          id="location"
                          value={reportForm.location}
                          onChange={(e) => setReportForm(prev => ({ ...prev, location: e.target.value }))}
                          placeholder="e.g. Near Edinburgh, Fife"
                          required
                          style={{ marginTop: '8px' }}
                        />
                      </div>

                      <div className="form-group">
                        <Label>Type of Concern *</Label>
                        <Select 
                          value={reportForm.concernType}
                          onValueChange={(value) => setReportForm(prev => ({ ...prev, concernType: value }))}
                        >
                          <SelectTrigger style={{ marginTop: '8px' }}>
                            <SelectValue placeholder="Select concern type..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="neglect">Neglect (lack of food/water/care)</SelectItem>
                            <SelectItem value="injury">Untreated injury or illness</SelectItem>
                            <SelectItem value="conditions">Poor living conditions</SelectItem>
                            <SelectItem value="safety">Safety hazards</SelectItem>
                            <SelectItem value="overcrowding">Overcrowding</SelectItem>
                            <SelectItem value="other">Other welfare concern</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="form-group">
                        <Label htmlFor="description">Description of Concern *</Label>
                        <Textarea 
                          id="description"
                          value={reportForm.description}
                          onChange={(e) => setReportForm(prev => ({ ...prev, description: e.target.value }))}
                          placeholder="Please provide as much detail as possible about what you have observed..."
                          required
                          style={{ marginTop: '8px', minHeight: '150px' }}
                        />
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                        <Checkbox 
                          id="anonymous"
                          checked={reportForm.anonymous}
                          onCheckedChange={(checked) => setReportForm(prev => ({ ...prev, anonymous: checked }))}
                        />
                        <Label htmlFor="anonymous" style={{ fontWeight: 400 }}>
                          Submit anonymously
                        </Label>
                      </div>

                      {!reportForm.anonymous && (
                        <div className="form-group">
                          <Label htmlFor="contactEmail">Your Email (optional)</Label>
                          <Input 
                            id="contactEmail"
                            type="email"
                            value={reportForm.contactEmail}
                            onChange={(e) => setReportForm(prev => ({ ...prev, contactEmail: e.target.value }))}
                            placeholder="In case we need to follow up"
                            style={{ marginTop: '8px' }}
                          />
                        </div>
                      )}

                      <button 
                        type="submit" 
                        className="btn-primary" 
                        style={{ width: '100%' }}
                        disabled={loading || !reportForm.concernType}
                      >
                        {loading ? (
                          <>
                            <Loader2 size={18} style={{ marginRight: '8px', animation: 'spin 1s linear infinite' }} />
                            Submitting...
                          </>
                        ) : (
                          <>
                            <Send size={18} style={{ marginRight: '8px' }} />
                            Submit Report
                          </>
                        )}
                      </button>
                    </form>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      <section className="cta-section">
        <div className="container">
          <h2 className="display-small">Interested in Joining?</h2>
          <p className="body-large" style={{ maxWidth: '600px', margin: '0 auto 32px' }}>
            If you run a livery yard and want to join our membership scheme, get in touch.
          </p>
          <Link to="/contact" className="btn-cta">Contact Us</Link>
        </div>
      </section>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </main>
  );
};

export default LiverySchemePage;
